import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversation, IChatConversationMessage, INewConversationData } from '@fi-shared/fi-chat/fi-chat';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { ILightWeightUserWithUnits } from '@fi-shared/models';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { TextFontSize } from '@fi-shared/fi-select-search/fi-select-search-item/fi-select-search-item.component';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { filter, takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils/is-defined';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import { FiChatSourceMessageType } from '@fi-app/shared/fi-chat/fi-chat-source-message-type';

enum FiChatConversationNewSelectSearchDisplayValues {
    DisplayValue = 'FullName',
    AdditionalDisplayValue = 'PreferredBusinessUnitName',
}

@Component({
    selector: 'fi-chat-conversation-new',
    templateUrl: './fi-chat-conversation-new.component.html',
    styleUrls: ['./fi-chat-conversation-new.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-new' },
    standalone: false,
})
export class FiChatConversationNewComponent implements OnInit, OnDestroy {
    @Input({ required: true }) conversation: IChatConversation;
    @Input({ required: true }) newMediaBankReferences$: Observable<IMediaBankFileReference[]> = new Observable<
        IMediaBankFileReference[]
    >();
    @Input() isDragging$: Observable<boolean> = new Observable<boolean>();
    @Output() startNewConversation = new EventEmitter<INewConversationData>();

    protected selectSearchDisplayValues = FiChatConversationNewSelectSearchDisplayValues;
    protected isSearchInputFocus = true;
    protected isMessageInputFocus = false;
    protected loading$ = new BehaviorSubject<boolean>(true);
    protected users$ = new BehaviorSubject<ILightWeightUserWithUnits[]>([]);
    protected selectedUserIds: number[] = [];
    protected newConversationMessage: IChatConversationMessage =
        this.fiChatHelperService.getNewConversationMessageInstance();
    protected readonly TextFontSize = TextFontSize;
    protected currentConversation: IChatConversation;
    // note: delay is set to prevent select focus before appearing animation for conversation container is finished
    protected readonly initialFocusDelay = 500;
    private ngOnDestroy$ = new Subject<void>();
    protected isEditMode = false;
    protected sourceMessage: IChatConversationMessage;

    constructor(
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.getAvailableUsersForConversation();
        this.setSelectedIds();
        this.onMediaBankReferenceDrop();
        this.subscribeOnForwardMessage();
    }

    private getAvailableUsersForConversation(): void {
        this.fiChatService.getAvailableUsersForConversation().then((users) => {
            this.users$.next(users);
            this.loading$.next(false);
        });
    }

    private subscribeOnForwardMessage(): void {
        this.fiChatHelperService.forwardMessage$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((messageData) => {
                    return isDefined(messageData) && !messageData.targetConversationId;
                }),
            )
            .subscribe((messageData) => {
                this.handleConversationMessageForward(messageData.message);
                this.cdr.markForCheck();
            });
    }

    private handleConversationMessageForward(message: IChatConversationMessage): void {
        this.isEditMode = true;
        const currentMessage = this.fiChatHelperService.isOnlySourceMessage(message) ? message.SourceMessage : message;
        this.sourceMessage = cloneDeep(currentMessage);
        this.newConversationMessage.SourceMessageType = FiChatSourceMessageType.Forwarded;
        this.newConversationMessage.SourceMessageId = currentMessage.Id;
    }

    protected cancelEditMode(): void {
        this.isEditMode = false;
        this.sourceMessage = undefined;
        this.newConversationMessage.SourceMessageType = undefined;
        this.newConversationMessage.SourceMessageId = undefined;
    }

    protected onSelectedUserIdsChanged(): void {
        this.currentConversation = undefined;
        setTimeout(() => {
            this.currentConversation = this.fiChatHelperService.findConversationBySelectedUserIds(this.selectedUserIds);
            /*this.fiChatService
                .findExistingConversations(this.selectedUserIds, FiChatConversationType.OneToOne)
                .then((result) => {
                });
            this.fiChatService
                .findExistingConversations(this.selectedUserIds, FiChatConversationType.Group)
                .then((result) => {
                });*/
        });
    }

    protected isCurrentConversationShown(): boolean {
        return isDefined(this.currentConversation);
    }

    protected onStartNewConversation(messageWithLinks?: IChatConversationMessage): void {
        if (!this.selectedUserIds.length) {
            return;
        }
        if (isDefined(this.sourceMessage)) {
            this.newConversationMessage.SourceMessage = this.sourceMessage;
        }
        this.startNewConversation.emit({
            type: this.selectedUserIds.length === 1 ? FiChatConversationType.OneToOne : FiChatConversationType.Group,
            selectedUserIds: this.selectedUserIds,
            message: isDefined(messageWithLinks) ? messageWithLinks : this.newConversationMessage,
        });
    }

    private onMediaBankReferenceDrop(): void {
        this.newMediaBankReferences$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((mediaBankFileReferences) => {
            this.newConversationMessage.MediaBankFileReferences = [
                ...this.newConversationMessage.MediaBankFileReferences,
                ...mediaBankFileReferences,
            ];
            this.newConversationMessage = { ...this.newConversationMessage };
            this.cdr.markForCheck();
        });
    }

    private setSelectedIds(): void {
        if (this.conversation.Users.length) {
            this.selectedUserIds = this.conversation.Users.map((conversationUser) => conversationUser.Id);
            this.isSearchInputFocus = false;
            this.isMessageInputFocus = true;
        }
    }

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
